import { useEffect, useRef, useState, type DragEvent } from 'react';
import classNames from 'classnames';
import { DetailViewCard } from '../CardWithTitle';
import {
  Attachment,
  ProgressHandler,
  getAttachmentSubject,
  getAttachmentLoadingSubject,
  fetchAttachments,
  fetchAttachmentUploadConfig,
  uploadAttachment,
  partitionAttachmentFiles
} from '../../../store/Attachment';
import { addError } from '../../../store/Error';
import { hasAnalystOrHigherPermissions } from '../../../store/User';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { AttachmentItem } from './AttachmentItem';
import { UploadingAttachmentRow } from './UploadingAttachmentRow';
import { AttachFilesModal } from './AttachFilesModal';

export interface DetailsAttachmentsProps {
  id: string;
  entityType: string;
}

const MAX_CONCURRENT_UPLOADS = 3;

interface QueuedUpload {
  file: File;
  entityId: string;
  entityType: string;
}

interface UploadingFile {
  key: string;
  file: File;
  progress: number;
  controller: AbortController;
  entityId: string;
  entityType: string;
}

export function DetailsAttachments({ id, entityType }: DetailsAttachmentsProps) {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const activeControllersRef = useRef<Set<AbortController>>(new Set());
  const isUnmountedRef = useRef<boolean>(false);
  const dragCounterRef = useRef<number>(0);
  const uploadQueueRef = useRef<QueuedUpload[]>([]);
  const activeUploadWorkersRef = useRef<number>(0);

  useEffect(() => {
    const activeControllers = activeControllersRef.current;
    return () => {
      isUnmountedRef.current = true;
      activeControllers.forEach((controller) => controller.abort());
      activeControllers.clear();
    };
  }, []);

  useEffect(() => {
    const attachmentSub = getAttachmentSubject(id, entityType).subscribe((data) => setAttachments(data));
    const loadingSub = getAttachmentLoadingSubject(id, entityType).subscribe((loading) => setIsFetching(loading));

    return () => {
      if (attachmentSub) attachmentSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [id, entityType]);

  useEffect(() => {
    if (id) fetchAttachments(id, entityType);
  }, [id, entityType]);

  useEffect(() => {
    void fetchAttachmentUploadConfig();
  }, []);

  useEffect(() => {
    const hasVisibleUploads = uploadingFiles.some((u) => u.entityId === id && u.entityType === entityType);
    if (attachments.length > 0 || hasVisibleUploads) setCollapsed(false);
  }, [attachments, uploadingFiles, id, entityType]);

  const startUpload = async (queuedUpload: QueuedUpload) => {
    const { file, entityId, entityType: uploadEntityType } = queuedUpload;
    const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
    const controller = new AbortController();
    activeControllersRef.current.add(controller);
    setUploadingFiles((prev) => [
      ...prev,
      { key, file, progress: 0, controller, entityId, entityType: uploadEntityType }
    ]);

    const progressHandler: ProgressHandler = (progressEvent) => {
      if (controller.signal.aborted) return;
      const percent =
        progressEvent.total && progressEvent.loaded
          ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
          : 0;
      setUploadingFiles((prev) => prev.map((u) => (u.key === key ? { ...u, progress: percent } : u)));
    };

    try {
      await uploadAttachment(file, entityId, uploadEntityType, progressHandler, controller.signal);
    } finally {
      activeControllersRef.current.delete(controller);
      if (!controller.signal.aborted) {
        setUploadingFiles((prev) => prev.filter((u) => u.key !== key));
      }
    }
  };

  const runUploadWorker = async () => {
    activeUploadWorkersRef.current += 1;
    try {
      while (uploadQueueRef.current.length && !isUnmountedRef.current) {
        await startUpload(uploadQueueRef.current.shift() as QueuedUpload);
      }
    } finally {
      activeUploadWorkersRef.current -= 1;
    }
  };

  const handleAttachFiles = (files: File[]) => {
    uploadQueueRef.current.push(...files.map((file) => ({ file, entityId: id, entityType })));
    const workersToStart = Math.max(
      0,
      Math.min(MAX_CONCURRENT_UPLOADS - activeUploadWorkersRef.current, uploadQueueRef.current.length)
    );
    for (let i = 0; i < workersToStart; i++) {
      void runUploadWorker();
    }
  };

  const handleCancelUpload = (uploadingFile: UploadingFile) => {
    uploadingFile.controller.abort();
    setUploadingFiles((prev) => prev.filter((u) => u.key !== uploadingFile.key));
  };

  const handleDroppedFiles = async (fileList: FileList) => {
    const files = Array.from(fileList);
    const config = await fetchAttachmentUploadConfig();
    const { validFiles, rejections } = partitionAttachmentFiles(files, config);
    if (rejections.length) addError('attachment', rejections.join('; '));
    if (validFiles.length) handleAttachFiles(validFiles);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingOver(false);
    if (event.dataTransfer.files?.length) void handleDroppedFiles(event.dataTransfer.files);
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
    }
  };

  const cardActions = () => {
    if (!hasAnalystOrHigherPermissions()) return undefined;

    return (
      <button
        type="button"
        className="flex items-center space-x-2"
        onClick={() => setIsModalOpen(true)}
        data-testid="new-attachment-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Attachment
        </TextSpan>
      </button>
    );
  };

  const visibleUploadingFiles = uploadingFiles.filter((u) => u.entityId === id && u.entityType === entityType);
  const isEmpty = !attachments.length && !visibleUploadingFiles.length;
  const canUpload = hasAnalystOrHigherPermissions();

  return (
    <>
      <DetailViewCard
        cardActions={cardActions()}
        collapsed={collapsed}
        enableCollapse
        key="attachments_details"
        title={`Attachments (${attachments.length})`}
        childrenWrapperClass="flex overflow-x-auto max-h-96">
        <div
          className={classNames('p-6 rounded-b-xl font-sans text-black w-full', {
            'border-indigo bg-indigo-light border-dashed border-2': isDraggingOver,
            'border-transparent border-dashed border-2': !isDraggingOver
          })}
          onDragEnter={canUpload ? handleDragEnter : undefined}
          onDragOver={canUpload ? (event) => event.preventDefault() : undefined}
          onDragLeave={canUpload ? handleDragLeave : undefined}
          onDrop={canUpload ? handleDrop : undefined}
          data-testid="attachments-drop-zone">
          {isFetching && visibleUploadingFiles.length === 0 && <ComponentSpinner label="Loading" />}
          {!isFetching && isEmpty && (
            <TextSpan
              size="xs"
              color="grey4">
              None
            </TextSpan>
          )}
          {visibleUploadingFiles.map((uploadingFile) => (
            <UploadingAttachmentRow
              fileName={uploadingFile.file.name}
              key={uploadingFile.key}
              onCancel={() => handleCancelUpload(uploadingFile)}
              progress={uploadingFile.progress}
            />
          ))}
          {!isFetching &&
            attachments.map((attachment) => (
              <AttachmentItem
                attachment={attachment}
                key={attachment.id}
              />
            ))}
        </div>
      </DetailViewCard>
      <AttachFilesModal
        onAttach={handleAttachFiles}
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
      />
    </>
  );
}
